/**
 * CitationReportPanel (FEAT-003 / ADR-019)
 *
 * Renders the "Source Citations" tab for a generated CaseMaker document.
 * Layout:
 *   - Summary bar: N verified · N need review · N unsupported
 *   - Stale banner when citationReport.stale === true
 *   - Per-claim cards (multi-source rows grouped by claimId)
 *
 * Read-only in v1. Source-document and care-log rows deep-link into the
 * CaseFile; other source types render as plain labels.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  Notebook,
  User,
  Calendar,
  MessageSquare,
  Info,
  ExternalLink,
} from 'lucide-react';
import type {
  Citation,
  CitationReport,
  ConfidenceLevel,
  SourceType,
  VerificationStatus,
} from '../../types/generatedDoc';

interface CitationReportPanelProps {
  /** The citation report from the generated doc. Pass null/undefined for empty state. */
  report: CitationReport | null | undefined;
  /** Parent CaseFile ID for constructing source deep links. */
  caseFileId: string;
  /** Optional extra class applied to the outer container. */
  className?: string;
}

/**
 * Visual treatment per verification status. Keeps colors consistent
 * across summary chips, claim badges, and the icon.
 */
const VERIFICATION_STYLES: Record<
  VerificationStatus,
  { bg: string; text: string; border: string; label: string; Icon: typeof CheckCircle2 }
> = {
  verified: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    label: 'Verified',
    Icon: CheckCircle2,
  },
  needs_review: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    label: 'Needs review',
    Icon: AlertCircle,
  },
  unsupported: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Unsupported',
    Icon: XCircle,
  },
};

const CONFIDENCE_LABEL: Record<ConfidenceLevel, string> = {
  direct: 'Direct',
  inferred: 'Inferred',
  contextual: 'Contextual',
};

const SOURCE_TYPE_META: Record<
  SourceType,
  { label: string; Icon: typeof FileText }
> = {
  source_document: { label: 'Uploaded document', Icon: FileText },
  care_log: { label: 'Care log entry', Icon: Notebook },
  case_file_field: { label: 'Care recipient profile', Icon: User },
  deadline: { label: 'Deadline', Icon: Calendar },
  user_description: { label: 'Caregiver description', Icon: MessageSquare },
  additional_context: { label: 'Additional context', Icon: Info },
};

/**
 * Build the deep-link target for a citation source. Returns null for
 * non-document sources (rendered as plain labels in v1).
 */
function buildSourceLink(citation: Citation, caseFileId: string): string | null {
  switch (citation.sourceType) {
    case 'source_document':
      // Documents page focuses on the doc when navigated with this state;
      // even without focus, the user lands on the right document list.
      return `/casefile/${caseFileId}/documents`;
    case 'care_log':
      return `/casefile/${caseFileId}/carelogs`;
    case 'deadline':
      return `/casefile/${caseFileId}/deadlines`;
    case 'case_file_field':
      return `/casefile/${caseFileId}`;
    case 'user_description':
    case 'additional_context':
    default:
      return null;
  }
}

/**
 * Group citations by claimId, preserving first-seen order. Returns one
 * entry per claim with all source rows underneath.
 */
function groupByClaim(citations: Citation[]): Array<{ claimId: string; claimText: string; sources: Citation[] }> {
  const order: string[] = [];
  const byId = new Map<string, { claimId: string; claimText: string; sources: Citation[] }>();
  for (const c of citations) {
    if (!byId.has(c.claimId)) {
      byId.set(c.claimId, { claimId: c.claimId, claimText: c.claimText, sources: [] });
      order.push(c.claimId);
    }
    byId.get(c.claimId)!.sources.push(c);
  }
  return order.map((id) => byId.get(id)!);
}

/**
 * For a multi-source claim, pick the "worst" verification status across
 * its rows to drive the claim-level badge. Unsupported > needs_review > verified.
 */
function rollupVerification(sources: Citation[]): VerificationStatus {
  if (sources.some((s) => s.verification === 'unsupported')) return 'unsupported';
  if (sources.some((s) => s.verification === 'needs_review')) return 'needs_review';
  return 'verified';
}

export function CitationReportPanel({
  report,
  caseFileId,
  className = '',
}: CitationReportPanelProps): JSX.Element {
  const groupedClaims = useMemo(
    () => (report ? groupByClaim(report.citations) : []),
    [report]
  );

  if (!report) {
    return (
      <div className={`card ${className}`}>
        <div className="card-body text-center py-8">
          <Info className="mx-auto mb-3 text-muted" size={32} />
          <p className="text-ink font-medium mb-1">No citations available</p>
          <p className="text-sm text-muted max-w-md mx-auto">
            This document doesn't have a citation report. It may have been
            generated before citations were enabled, or the AI fell back to
            the legacy generation path.
          </p>
        </div>
      </div>
    );
  }

  const { summary, citations, stale } = report;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Stale banner */}
      {stale && (
        <div
          className="card border-amber-200 bg-amber-50"
          role="alert"
          aria-live="polite"
        >
          <div className="card-body py-3 flex items-start gap-3">
            <AlertTriangle
              className="text-amber-600 flex-shrink-0 mt-0.5"
              size={18}
              aria-hidden="true"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                Citations may be outdated
              </p>
              <p className="text-sm text-amber-700 mt-0.5">
                The letter has been edited since these citations were generated.
                Regenerate to refresh.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary bar */}
      <div className="card">
        <div className="card-body py-4">
          <h3 className="font-semibold text-ink mb-3">Citation summary</h3>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <SummaryChip
              count={summary.verifiedCount}
              total={summary.totalCount}
              status="verified"
            />
            <SummaryChip
              count={summary.needsReviewCount}
              total={summary.totalCount}
              status="needs_review"
            />
            <SummaryChip
              count={summary.unsupportedCount}
              total={summary.totalCount}
              status="unsupported"
            />
            <span className="text-muted ml-auto">
              {summary.totalCount}{' '}
              {summary.totalCount === 1 ? 'claim' : 'claims'} cited
            </span>
          </div>
          {/* BUG-119: surface off-enum sourceType events when the model emitted
              an unrecognized category that was tolerated and normalized. */}
          {summary.offEnumSourceTypeCount > 0 && (
            <p
              className="text-xs text-muted mt-2"
              data-testid="off-enum-source-type-note"
            >
              {summary.offEnumSourceTypeCount}{' '}
              citation{summary.offEnumSourceTypeCount === 1 ? '' : 's'} had an
              unrecognized source type that was normalized automatically.
            </p>
          )}
        </div>
      </div>

      {/* Per-claim cards */}
      {citations.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-6">
            <p className="text-sm text-muted">
              No claims were extracted for this document.
            </p>
          </div>
        </div>
      ) : (
        <ol className="space-y-3 list-none pl-0">
          {groupedClaims.map((claim, index) => (
            <ClaimCard
              key={claim.claimId}
              index={index + 1}
              claim={claim}
              caseFileId={caseFileId}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

interface SummaryChipProps {
  count: number;
  total: number;
  status: VerificationStatus;
}

function SummaryChip({ count, status }: SummaryChipProps): JSX.Element {
  const style = VERIFICATION_STYLES[status];
  const Icon = style.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${style.bg} ${style.text} ${style.border}`}
    >
      <Icon size={14} aria-hidden="true" />
      <span className="font-medium">{count}</span>
      <span>{style.label.toLowerCase()}</span>
    </span>
  );
}

interface ClaimCardProps {
  index: number;
  claim: { claimId: string; claimText: string; sources: Citation[] };
  caseFileId: string;
}

function ClaimCard({ index, claim, caseFileId }: ClaimCardProps): JSX.Element {
  const verification = rollupVerification(claim.sources);
  const style = VERIFICATION_STYLES[verification];
  const Icon = style.Icon;

  return (
    <li className={`card border ${style.border}`}>
      <div className="card-body space-y-3">
        <div className="flex items-start gap-3">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${style.bg} ${style.text} border ${style.border} flex-shrink-0`}
          >
            <Icon size={12} aria-hidden="true" />
            {style.label}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted uppercase tracking-wide mb-1">
              Claim {index}
            </p>
            <p className="text-ink leading-snug">{claim.claimText}</p>
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t border-soft-neutral">
          <p className="text-xs text-muted uppercase tracking-wide">
            {claim.sources.length === 1
              ? 'Source'
              : `Sources (${claim.sources.length})`}
          </p>
          {claim.sources.map((src, i) => (
            <SourceRow
              key={`${claim.claimId}-${i}`}
              citation={src}
              caseFileId={caseFileId}
            />
          ))}
        </div>
      </div>
    </li>
  );
}

interface SourceRowProps {
  citation: Citation;
  caseFileId: string;
}

function SourceRow({ citation, caseFileId }: SourceRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const meta = SOURCE_TYPE_META[citation.sourceType];
  const SourceIcon = meta.Icon;
  const link = buildSourceLink(citation, caseFileId);
  const label = citation.sourceLabel || formatFallbackLabel(citation);

  return (
    <div className="bg-soft-neutral/40 rounded-md px-3 py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <SourceIcon size={14} className="text-muted flex-shrink-0" aria-hidden="true" />
        {link ? (
          <Link
            to={link}
            className="text-sm text-evergreen hover:underline inline-flex items-center gap-1"
          >
            {label}
            <ExternalLink size={11} aria-hidden="true" />
          </Link>
        ) : (
          <span className="text-sm text-ink">{label}</span>
        )}
        <span className="text-xs text-muted">·</span>
        <span className="text-xs text-muted">{meta.label}</span>
        <span className="text-xs text-muted">·</span>
        <span className="text-xs text-muted">
          {CONFIDENCE_LABEL[citation.confidence]}
        </span>

        {citation.excerpt && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="ml-auto inline-flex items-center gap-1 text-xs text-muted hover:text-ink"
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                <ChevronDown size={12} aria-hidden="true" />
                Hide excerpt
              </>
            ) : (
              <>
                <ChevronRight size={12} aria-hidden="true" />
                Show excerpt
              </>
            )}
          </button>
        )}
      </div>

      {expanded && citation.excerpt && (
        <blockquote className="mt-2 pl-3 border-l-2 border-soft-neutral text-sm text-ink/80 whitespace-pre-wrap">
          {citation.excerpt}
        </blockquote>
      )}

      {citation.reviewerNote && (
        <p className="mt-2 text-xs text-amber-700 italic">
          Note: {citation.reviewerNote}
        </p>
      )}
    </div>
  );
}

/**
 * When the server didn't supply a sourceLabel (rare, mostly for sentinel
 * IDs like USER_DESCRIPTION), build something readable from the type.
 */
function formatFallbackLabel(citation: Citation): string {
  if (citation.sourceId === 'UNMAPPED') return 'Source not found in context';
  switch (citation.sourceType) {
    case 'user_description':
      return "Caregiver's situation description";
    case 'additional_context':
      return 'Additional context provided';
    case 'case_file_field':
      return 'Care recipient profile';
    case 'deadline':
      return 'Related deadline';
    default:
      return citation.sourceId;
  }
}

export default CitationReportPanel;
